import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useStore } from '../store/useStore';
import { useWebRTC } from '../hooks/useWebRTC';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRotate, faMicrophone,faPhoneSlash, faMicrophoneSlash, faVideo, faVideoSlash } from '@fortawesome/free-solid-svg-icons';

// ─────────────────────────────────────────────────────────────────────────────
// COMPOSANT : Flèche SVG directionnelle
// ─────────────────────────────────────────────────────────────────────────────
function ArrowSVG({ x1, y1, x2, y2, color, strokeWidth = 2, preview = false }) {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const len = Math.sqrt(dx * dx + dy * dy);
  if (len < 1) return null;

  const angle = Math.atan2(dy, dx);
  const headLen = Math.min(20, len * 0.4);

  let wingAngle;
  if (len < 30) {
    wingAngle = 0.7;
  } else if (len < 80) {
    wingAngle = 0.5;
  } else {
    wingAngle = 0.25;
  }

  const p1x = x2 - headLen * Math.cos(angle - wingAngle);
  const p1y = y2 - headLen * Math.sin(angle - wingAngle);
  const p2x = x2 - headLen * Math.cos(angle + wingAngle);
  const p2y = y2 - headLen * Math.sin(angle + wingAngle);

  return (
    <g opacity={preview ? 0.65 : 1}>
      <line
        x1={x1} y1={y1}
        x2={x2} y2={y2}
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        filter="drop-shadow(1px 2px 3px rgba(0,0,0,0.75))"
        vectorEffect="non-scaling-stroke"
      />
      <polygon
        points={`${p1x},${p1y} ${x2},${y2} ${p2x},${p2y}`}
        fill={color}
        stroke={color}
        strokeWidth={strokeWidth * 0.6}
        strokeLinejoin="round"
        filter="drop-shadow(1px 2px 3px rgba(0,0,0,0.75))"
        vectorEffect="non-scaling-stroke"
      />
    </g>
  );
}

// COMPOSANT : Tracé libre SVG
function FreehandPath({ points, color, strokeWidth = 2, preview = false }) {
  if (!points || points.length < 2) return null;

  const d = points
    .map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x},${p.y}`)
    .join(' ');

  return (
    <path
      d={d}
      stroke={color}
      strokeWidth={strokeWidth}
      vectorEffect="non-scaling-stroke"
      strokeLinecap="round"
      strokeLinejoin="round"
      fill="none"
      opacity={preview ? 0.65 : 1}
      filter="drop-shadow(1px 1px 2px rgba(0,0,0,0.6))"
    />
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// OPERATOR VIEW - Version corrigée
// ─────────────────────────────────────────────────────────────────────────────
export default function OperatorView() {
  const { sessionId } = useParams();

  const {
    localStream,
    remoteStream,
    connectionState,
    isConnected,
    cameraError,
    localVideoRef,
    remoteVideoRef,
    socketRef,
    peerConnectionRef,
    logs,
  } = useWebRTC(sessionId, 'operator');

  const annotations = useStore((state) => state.annotations);
  const addAnnotation = useStore((state) => state.addAnnotation);
  const removeAnnotation = useStore((state) => state.removeAnnotation);

  const facingModeRef = useRef("user");
  
  // État pour les contrôles audio/vidéo
  const [isMuted, setIsMuted] = useState(false);
  const [isVideoOff, setIsVideoOff] = useState(false);

  // Rotation caméra
  const switchCamera = async () => {
    if (!localStream) {
      console.warn("switchCamera: localStream indisponible");
      return;
    }

    try {
      const newMode = facingModeRef.current === "user" ? "environment" : "user";
      facingModeRef.current = newMode;

      if (peerConnectionRef?.current) {
        const sender = peerConnectionRef.current.getSenders()
          .find(s => s.track?.kind === "video");
        
        const newStream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { exact: newMode } },
          audio: true,
        });
        
        const newVideoTrack = newStream.getVideoTracks()[0];
        
        if (sender && newVideoTrack) {
          await sender.replaceTrack(newVideoTrack);
        }
        
        localStream.getVideoTracks().forEach(t => t.stop());
        
        if (localVideoRef.current) {
          localVideoRef.current.srcObject = newStream;
        }
      } 
      else {
        const newStream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { exact: newMode } },
          audio: true,
        });
        
        const oldVideoTrack = localStream.getVideoTracks()[0];
        const newVideoTrack = newStream.getVideoTracks()[0];
        
        if (oldVideoTrack && newVideoTrack) {
          localStream.removeTrack(oldVideoTrack);
          localStream.addTrack(newVideoTrack);
          oldVideoTrack.stop();
        }
        
        if (localVideoRef.current) {
          localVideoRef.current.srcObject = localStream;
        }
      }
    } catch (err) {
      console.error("Camera switch error:", err);
    }
  };

  // Couper/rétablir le micro
  const toggleMicrophone = () => {
    if (!localStream) return;
    
    const audioTrack = localStream.getAudioTracks()[0];
    if (audioTrack) {
      const newState = !audioTrack.enabled;
      audioTrack.enabled = newState;
      setIsMuted(!newState);
    }
  };

  // Couper/rétablir la caméra
  const toggleVideo = () => {
    if (!localStream) return;
    
    const videoTrack = localStream.getVideoTracks()[0];
    if (videoTrack) {
      const newState = !videoTrack.enabled;
      videoTrack.enabled = newState;
      setIsVideoOff(!newState);
    }
  };

  // SOCKETS ANNOTATIONS
  useEffect(() => {
    const socket = socketRef?.current;
    if (!socket) {
      console.warn("OPERATEUR - Socket non disponible");
      return;
    }

    const handleNew = (annotation) => {
      console.log("OPÉRATEUR REÇOIT :", annotation.label || annotation.type);
      addAnnotation(annotation);
    };

    const handleDel = (id) => {
      console.log("OPÉRATEUR - Suppression :", id);
      removeAnnotation(id);
    };

    const handleSync = (annots) => {
      console.log(`OPÉRATEUR - Sync reçu (${annots.length} annotations)`);
      useStore.getState().clearAnnotations();
      annots.forEach(a => addAnnotation(a));
    };

    socket.on('new-annotation', handleNew);
    socket.on('annotation-deleted', handleDel);
    socket.on('sync-annotations', handleSync);

    if (isConnected && sessionId) {
      setTimeout(() => {
        socket.emit('request-sync-annotations', { sessionId });
      }, 800);
    }

    return () => {
      socket.off('new-annotation', handleNew);
      socket.off('annotation-deleted', handleDel);
      socket.off('sync-annotations', handleSync);
    };
  }, [socketRef, addAnnotation, removeAnnotation, isConnected, sessionId]);

  // Gestion fin d'appel
  useEffect(() => {
    const socket = socketRef?.current;
    if (!socket) return;

    socket.on('call-ended', () => {
      if (localStream) {
        localStream.getTracks().forEach(track => track.stop());
      }

      if (remoteStream) {
        remoteStream.getTracks().forEach(track => track.stop());
      }

      window.location.href = '/';
    });

    return () => {
      socket.off('call-ended');
    };
  }, [socketRef, localStream, remoteStream]);

  // Mode normal - affichage
  return (
    <div style={{
      width: '100vw',
      //  height: '100vh',
       height: 'calc(100vh - 50px)',
       position: 'relative',
      background: '#000', overflow: 'hidden'
    }}>
      {/* Vidéo opérateur */}
      <video
        ref={localVideoRef}
        autoPlay
        playsInline
        muted
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          zIndex: 1,
          transform: 'translateY(-10px)'
        }}
      />

      {/* Bouton rotation caméra */}
      <button
        onClick={switchCamera}
        style={{
          position: 'absolute',
          top: 20,
          right: 20,
          zIndex: 5,
          width: '42px',
          height: '42px',
          borderRadius: '50%',
          border: 'none',
          background: 'rgba(0,0,0,0.55)',
          color: 'white',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backdropFilter: 'blur(6px)'
        }}
      >
        <FontAwesomeIcon icon={faRotate} />
      </button>

      {/* Bouton couper micro */}
      <button
        onClick={toggleMicrophone}
        style={{
          position: 'absolute',
          bottom: 20,
          left: 20,
          zIndex: 5,
          width: '50px',
          height: '50px',
          borderRadius: '50%',
          border: 'none',
          background: isMuted ? 'rgba(220, 53, 69, 0.8)' : 'rgba(0,0,0,0.55)',
          color: 'white',
          cursor: 'pointer',
          backdropFilter: 'blur(6px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <FontAwesomeIcon icon={isMuted ? faMicrophoneSlash : faMicrophone} size="lg" />
      </button>

      {/* Bouton quitter la session - rond comme WhatsApp */}
<button
  onClick={() => {
    if (window.confirm('Voulez-vous vraiment quitter la session ?')) {
      const socket = socketRef?.current;
      if (socket) {
        socket.emit('end-call', { sessionId });
      }
      if (localStream) {
        localStream.getTracks().forEach(track => track.stop());
      }
      if (remoteStream) {
        remoteStream.getTracks().forEach(track => track.stop());
      }
      window.location.href = '/';
    }
  }}
  style={{
    position: 'absolute',
    bottom: 20,
    left: 160,
    zIndex: 100,
    width: '50px',
    height: '50px',
    borderRadius: '50%',
    background: '#e94560',
    color: 'white',
    border: 'none',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
    transition: 'transform 0.2s, background 0.2s',
  }}
  onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
  onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
>
  <FontAwesomeIcon icon={faPhoneSlash} color="white" size="lg" />
</button>

      {/* Bouton couper caméra */}
      <button
        onClick={toggleVideo}
        style={{
          position: 'absolute',
          bottom: 20,
          left: 90,
          zIndex: 5,
          width: '50px',
          height: '50px',
          borderRadius: '50%',
          border: 'none',
          background: isVideoOff ? 'rgba(220, 53, 69, 0.8)' : 'rgba(0,0,0,0.55)',
          color: 'white',
          cursor: 'pointer',
          backdropFilter: 'blur(6px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <FontAwesomeIcon icon={isVideoOff ? faVideoSlash : faVideo} size="lg" />
      </button>

      {/* Vidéo expert */}
      {remoteStream && (
        <video
          ref={remoteVideoRef}
          autoPlay
          playsInline
          style={{
            position: 'absolute', bottom: 20, right: 20,
            width: '200px', height: '150px', objectFit: 'cover',
            zIndex: 2, borderRadius: '10px', border: '3px solid #4CAF50'
          }}
        />
      )}

      {/* Overlay SVG pour flèches et marqueurs */}
      <svg
        style={{
          position: 'absolute', top: 0, left: 0,
          width: '100%', height: '100%',
          pointerEvents: 'none',
          zIndex: 15,
          overflow: 'visible',
        }}
        viewBox="0 0 100 100"
        preserveAspectRatio="none"
      >
        {annotations.map((ann) => {
          if (ann.type === 'arrow' && ann.x2 !== undefined) {
            return (
              <ArrowSVG
                key={ann.id}
                x1={ann.x * 100}
                y1={ann.y * 100}
                x2={ann.x2 * 100}
                y2={ann.y2 * 100}
                color={ann.color || '#ff0000'}
                strokeWidth={ann.strokeWidth || 2}
              />
            );
          }
          
          if (ann.type === 'marker' && ann.points && ann.points.length >= 2) {
            return (
              <FreehandPath
                key={ann.id}
                points={ann.points}
                color={ann.color || '#ffff00'}
                strokeWidth={ann.strokeWidth || 2}
              />
            );
          }
          return null;
        })}
      </svg>

      {/* Annotations texte */}
      {annotations.map((ann) => {
        if (ann.type === 'text') {
          return (
            <div
              key={ann.id}
              style={{
                position: 'absolute',
                left: `${ann.x * 100}%`,
                top: `${ann.y * 100}%`,
                transform: 'translate(-50%, -50%)',
                background: ann.color || '#e94560',
                color: 'white',
                padding: '8px 14px',
                borderRadius: '8px',
                fontSize: '15px',
                fontWeight: '600',
                zIndex: 20,
                pointerEvents: 'none',
                boxShadow: '0 4px 20px rgba(0,0,0,0.7)',
                whiteSpace: 'nowrap',
              }}
            >
              {ann.label || 'Texte'}
            </div>
          );
        }
        return null;
      })}

      {/* HUD - Version mobile */}
      <div style={{
        position: 'absolute', top: 10, left: 10, zIndex: 40,
        color: 'white', background: 'rgba(0,0,0,0.7)',
        padding: '6px 10px', borderRadius: '8px', fontSize: '10px',
        maxWidth: '45%',
      }}>
        <div style={{ fontWeight: 'bold', fontSize: '11px', marginBottom: '2px' }}>👤 Opérateur</div>
        <div>ID: {sessionId?.slice(0,7)}</div>
        <div>{isConnected ? ' Expert connecté' : ' En attente expert...'}</div>
        <div> {annotations.length} annotations</div>
      </div>
    </div>
  );
}